import React, {useMemo} from 'react';

import {
    FloatingFocusManager,
    FloatingNode,
    FloatingOverlay,
    FloatingPortal,
} from '@floating-ui/react';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {useDialogFloating} from '../../hooks/useDialogFloating';
import {DialogContext} from '../../context/dialog-context';

import dialogStyles from '../../dialog.module.styl';

import type {
    FloatingFocusManagerProps,
    FloatingPortalProps,
    UseDismissProps,
    UseInteractionsReturn,
    UseRoleProps,
} from '@floating-ui/react';
import type {ComponentProps, ReactNode} from 'react';


const classNames = withCssModule(dialogStyles);
const classes = hashedClasses(dialogStyles);

type OverlayPropsInternal = {
    /** Content rendered inside the modal overlay (typically `<Dialog>` children). */
    children: ReactNode,
    /**
     * Props forwarded to `FloatingPortal`. Use `root` or `id` to control where the
     * dialog is injected in the DOM (defaults to `document.body`).
     */
    portalProps?: FloatingPortalProps,
    /**
     * Props forwarded to Floating UI's `useDismiss`. Use `outsidePress`, `escapeKey`,
     * or `referencePress` to configure when and how the dialog can be closed.
     */
    dismissProps?: UseDismissProps,
    /**
     * Props forwarded to `FloatingFocusManager`. Use `initialFocus`, `returnFocus`,
     * or `order` to tune keyboard focus-trap behavior. `modal` is always `true`.
     */
    floatingFocusManagerProps?: FloatingFocusManagerProps,
    /**
     * Props forwarded to `FloatingOverlay`. Use `lockScroll` (enabled by default)
     * or `className` to customize the backdrop element.
     */
    floatingOverlayProps?: ComponentProps<typeof FloatingOverlay>,
    /**
     * Props forwarded to Floating UI's `useRole`. Use `role` to set the ARIA role
     * (`"dialog"` or `"alertdialog"`) and `enabled` to toggle `aria-modal`.
     */
    roleProps?: UseRoleProps,
};

type WithAnchor = {
    /**
     * Render prop for an optional trigger element. Receives `anchorRef` and
     * `getAnchorProps` — spread them onto your trigger to link it to the overlay.
     * When provided, `shown` becomes optional and the overlay can manage its own
     * open state via the anchor interactions.
     */
    anchor: (props: {
        anchorRef: (node: Element | null) => void,
        getAnchorProps: UseInteractionsReturn['getReferenceProps'],
    }) => ReactNode,
    /** Optional controlled open state when an `anchor` is provided. */
    shown?: boolean,
    /** Callback to update the `shown` state. */
    setShown?: (shown: boolean) => void,
} & OverlayPropsInternal;

type WithoutAnchor = {
    anchor?: undefined,
    /** Controlled open/close state. `true` mounts the overlay; `false` unmounts it. */
    shown: boolean,
    /** Callback to update the `shown` state. */
    setShown: (shown: boolean) => void,
} & OverlayPropsInternal;

export type OverlayProps = WithAnchor | WithoutAnchor;

/**
 * Low-level overlay primitive that mounts a full-viewport backdrop with a focus
 * trap, scroll lock, and portal for modal-style dialogs.
 *
 * Handles the complete modal lifecycle via `useDialogFloating`: mounting/unmounting
 * with an animation, z-index stacking, `FloatingPortal`, `FloatingOverlay` (with
 * `lockScroll`), and `FloatingFocusManager` (`modal: true`). The open state is
 * controlled via `shown`/`setShown`. An optional `anchor` render prop lets you
 * link a trigger element.
 *
 * Prefer `<Dialog>` or `<AlertDialog>` over `Overlay` directly — they add the
 * panel styling and ARIA roles on top of this primitive.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/overlays-dialog--documentation}
 */
export function Overlay({
    shown,
    anchor,
    children,
    portalProps,
    dismissProps,
    roleProps,
    floatingFocusManagerProps,
    floatingOverlayProps,
    setShown,
}: OverlayProps) {

    const {
        nodeId,
        floatingStyles,
        refs,
        zIndex,
        isMounted,
        styles,
        context,
        getReferenceProps,
        getFloatingProps,
    } = useDialogFloating({
        shown,
        setShown,
        isAnchor: !!anchor,
        roleProps,
        dismissProps,
    });

    const contextValue = useMemo(() => ({
        onClose: () => setShown?.(false),
    }), [setShown]);

    return (
        <DialogContext.Provider value={contextValue}>
            {anchor ? anchor({
                anchorRef: refs.setReference,
                getAnchorProps: _props => ({
                    ..._props,
                    ...getReferenceProps(_props), // props is merged twice cause getReferenceProps returns only own props, not all props from props
                }),
            }) : null}

            <FloatingNode id={nodeId}>
                {isMounted ? (
                    <FloatingPortal {...portalProps}>
                        <FloatingOverlay
                            lockScroll={true}
                            {...floatingOverlayProps}
                            className={classNames(classes['dialog-overlay'], floatingOverlayProps?.className)}
                            style={{
                                ...floatingStyles,
                                ...styles,
                                transform: 'translate(0px, 0px)',
                                zIndex,
                                ...floatingOverlayProps?.style,
                            }}>
                            <FloatingFocusManager
                                order={['floating', 'content']}
                                {...floatingFocusManagerProps}
                                context={context}
                                modal={true}>
                                <div
                                    ref={refs.setFloating}
                                    {...getFloatingProps()}
                                    aria-modal={roleProps?.enabled}
                                    style={{
                                        ...styles,
                                    }}>
                                    {children}
                                </div>
                            </FloatingFocusManager>
                        </FloatingOverlay>
                    </FloatingPortal>
                )
                    : null}
            </FloatingNode>
        </DialogContext.Provider>
    );
}
